import { FC } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import type { ReactDatePickerProps } from 'react-datepicker';
import it from 'date-fns/locale/it';
import dayjs from 'dayjs';
import { CalendarBlank, Info, X } from '@phosphor-icons/react';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './MultiDateInlinePicker.module.scss';

registerLocale('it', it);

// BUG mismatch tipi @types/react-datepicker (dipendenza transitiva su @types/react v19)
// vs @types/react v18 del progetto: `DatePicker` non è più assegnabile a JSX.ElementType.
// Wrapper funzionale tipizzato per bypassare l'incompatibilità di classe senza usare `any`.
const TypedDatePicker = DatePicker as unknown as (
  props: ReactDatePickerProps
) => JSX.Element;

export interface MultiDateInlinePickerProps {
  /** Array di date in formato ISO YYYY-MM-DD */
  value: string[];
  /** Callback invocata con il nuovo array (toggle on click) */
  onChange: (dates: string[]) => void;
  /** Label opzionale sopra il calendario */
  label?: string;
  /** Hint mostrato sotto il calendario quando nessuna data è selezionata */
  emptyHint?: string;
  /** Limiti opzionali di selezione */
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Calendario inline multi-selezione (toggle on click).
 *
 * Pattern estratto da AssignWorkoutModal per riuso: ovunque sia richiesto
 * selezionare una lista di giorni discreti (assegnazione schede, eventi
 * test ricorrenti, ecc.) → un click sul giorno aggiunge/rimuove dalla
 * lista, sotto il calendario compaiono i chips delle date selezionate
 * con bottone X per rimozione singola.
 */
export const MultiDateInlinePicker: FC<MultiDateInlinePickerProps> = ({
  value,
  onChange,
  label,
  emptyHint = 'Clicca sul calendario per selezionare le date',
  minDate,
  maxDate,
}) => {
  const validDates = value.filter(Boolean);
  const highlightDates = validDates.map((d) => new Date(d + 'T12:00:00'));
  const sortedDates = [...validDates].sort();
  const hasDates = sortedDates.length > 0;

  const toggleDate = (date: Date | null) => {
    if (!date) return;
    const iso = dayjs(date).format('YYYY-MM-DD');
    const next = validDates.includes(iso)
      ? validDates.filter((d) => d !== iso)
      : [...validDates, iso];
    onChange(next);
  };

  const removeDate = (iso: string) => {
    onChange(validDates.filter((d) => d !== iso));
  };

  return (
    <div className={styles.wrapper}>
      {label && (
        <div className={styles.labelRow}>
          <p className={styles.label}>
            <CalendarBlank size={14} weight="bold" className={styles.labelIcon} />
            {label}
          </p>
          {hasDates && (
            <span className={styles.counter}>
              {sortedDates.length} {sortedDates.length === 1 ? 'data' : 'date'}
            </span>
          )}
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.calendarBox}>
          <TypedDatePicker
            inline
            locale="it"
            onChange={toggleDate}
            minDate={minDate}
            maxDate={maxDate}
            highlightDates={[
              { 'react-datepicker__day--highlighted-custom': highlightDates },
            ]}
          />
        </div>

        {hasDates ? (
          <>
            <div className={styles.divider} />
            <div className={styles.selectedSection}>
              <p className={styles.selectedTitle}>Date selezionate</p>
              <div className={styles.chipsRow}>
                {sortedDates.map((d) => (
                  <span key={d} className={styles.chip}>
                    <span className={styles.chipText}>{dayjs(d).format('ddd D MMM')}</span>
                    <button
                      type="button"
                      className={styles.chipRemove}
                      onClick={() => removeDate(d)}
                      title="Rimuovi data"
                      aria-label={`Rimuovi ${dayjs(d).format('D MMMM YYYY')}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <p className={styles.emptyHint}>
            <Info size={14} weight="bold" className={styles.emptyHintIcon} />
            {emptyHint}
          </p>
        )}
      </div>
    </div>
  );
};

export default MultiDateInlinePicker;
